(() => {
  'use strict';

  const qs = (selector, root = document) => root.querySelector(selector);
  const qsa = (selector, root = document) => [...root.querySelectorAll(selector)];
  const body = document.body;
  const scrim = qs('[data-site-scrim]');
  const currency = window.Shopify?.currency?.active || 'USD';

  const money = (cents = 0) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency, maximumFractionDigits: cents % 100 === 0 ? 0 : 2
      }).format(cents / 100);
    } catch (_) { return `$${(cents / 100).toFixed(2)}`; }
  };

  const escapeHTML = (value = '') => String(value).replace(/[&<>'"]/g, char => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  }[char]));

  const lock = () => { body.classList.add('is-locked'); if (scrim) scrim.hidden = false; };
  const unlock = () => {
    body.classList.remove('is-locked');
    if (scrim) scrim.hidden = true;
    qsa('[data-account-popover]').forEach(el => { el.hidden = true; });
  };

  function closeAll() {
    qsa('.modal-shell').forEach(el => { el.hidden = true; });
    qsa('[data-cart-drawer]').forEach(el => { el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true'); });
    qsa('[data-mobile-menu]').forEach(el => { el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true'); });
    qsa('[data-account-popover]').forEach(el => { el.hidden = true; });
    unlock();
  }

  function initHeader() {
    const header = qs('[data-site-header]');
    if (!header) return;
    const update = () => header.classList.toggle('is-scrolled', window.scrollY > 12);
    update();
    window.addEventListener('scroll', update, { passive: true });

    qsa('[data-logo-cycle]').forEach(cycle => {
      const images = qsa('.logo-cycle__image', cycle);
      if (images.length < 2 || !cycle.classList.contains('is-animated')) return;
      let active = 0;
      images[0].classList.add('is-active');
      window.setInterval(() => {
        images[active].classList.remove('is-active');
        active = (active + 1) % images.length;
        images[active].classList.add('is-active');
      }, Number(cycle.dataset.speed || 900));
    });

    const mobileMenu = qs('[data-mobile-menu]');
    qs('[data-mobile-menu-open]')?.addEventListener('click', () => {
      mobileMenu?.classList.add('is-open'); mobileMenu?.setAttribute('aria-hidden', 'false'); lock();
    });
    qs('[data-mobile-menu-close]')?.addEventListener('click', closeAll);

    const account = qs('[data-account-popover]');
    qs('[data-open-account]')?.addEventListener('click', event => {
      event.stopPropagation();
      if (!account) return;
      account.hidden = !account.hidden;
      if (!account.hidden) lock(); else unlock();
    });

    qs('[data-open-search]')?.addEventListener('click', openSearch);
    qs('[data-open-cart]')?.addEventListener('click', openCart);
  }

  function initPageTransition() {
    const overlay = qs('[data-page-transition]');
    if (!overlay || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    overlay.classList.add('is-entering');
    const speed = parseInt(getComputedStyle(overlay).getPropertyValue('--transition-speed'), 10) || 650;
    window.setTimeout(() => overlay.classList.remove('is-entering'), speed + 80);

    document.addEventListener('click', event => {
      const link = event.target.closest('a[href]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (link.target === '_blank' || link.hasAttribute('download')) return;
      const next = new URL(link.href, window.location.href);
      if (next.origin !== window.location.origin || next.pathname === window.location.pathname && next.search === window.location.search && next.hash) return;
      if (link.closest('[data-no-transition]')) return;
      event.preventDefault();
      overlay.classList.remove('is-entering');
      overlay.classList.add('is-leaving');
      window.setTimeout(() => { window.location.href = next.href; }, Math.max(420, speed - 70));
    });
  }

  function initProductCards(root = document) {
    qsa('[data-product-card]', root).forEach(card => {
      if (card.dataset.ready === 'true') return;
      card.dataset.ready = 'true';
      const images = qsa('.product-card__image', card);
      if (!images.length) return;
      let active = 0;
      const show = index => {
        images.forEach(image => image.classList.remove('is-active'));
        active = (index + images.length) % images.length;
        images[active].classList.add('is-active');
      };
      show(0);
      qs('[data-card-prev]', card)?.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); show(active - 1); });
      qs('[data-card-next]', card)?.addEventListener('click', event => { event.preventDefault(); event.stopPropagation(); show(active + 1); });
    });
  }

  function openCart() {
    const drawer = qs('[data-cart-drawer]');
    if (!drawer) return;
    drawer.classList.add('is-open'); drawer.setAttribute('aria-hidden', 'false'); lock();
  }

  function renderCart(cart) {
    qsa('[data-cart-count]').forEach(el => el.textContent = cart.item_count);
    qsa('[data-header-cart-count]').forEach(el => { el.textContent = cart.item_count; el.classList.toggle('is-empty', cart.item_count === 0); });
    qsa('[data-cart-total]').forEach(el => el.textContent = money(cart.total_price));
    const holder = qs('[data-cart-items]');
    if (!holder) return;
    if (!cart.items.length) {
      holder.innerHTML = '<p class="cart-drawer__empty">Your cart is empty.</p>';
      return;
    }
    holder.innerHTML = cart.items.map((item, index) => {
      const image = item.featured_image?.url || item.image || '';
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<span>${escapeHTML(item.variant_title)}</span>` : '';
      return `<div class="cart-line" data-line="${index + 1}">
        ${image ? `<img src="${escapeHTML(image)}&width=160" alt="${escapeHTML(item.product_title)}" width="80" height="100">` : ''}
        <div class="cart-line__info"><a href="${escapeHTML(item.url)}">${escapeHTML(item.product_title)}</a>${variant}</div>
        <span class="cart-line__price">${money(item.final_line_price)}</span>
        <div class="quantity-control"><button type="button" data-cart-minus aria-label="Decrease quantity">−</button><span>${item.quantity}</span><button type="button" data-cart-plus aria-label="Increase quantity">+</button></div>
        <button class="text-button" type="button" data-cart-remove>Remove</button>
      </div>`;
    }).join('');
    bindCartLines();
  }

  async function refreshCart(open = false) {
    const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    const cart = await response.json();
    renderCart(cart);
    if (open) openCart();
    return cart;
  }

  async function changeCartLine(line, quantity) {
    const response = await fetch('/cart/change.js', {
      method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ line, quantity })
    });
    if (!response.ok) throw new Error('Unable to update cart');
    renderCart(await response.json());
  }

  function bindCartLines() {
    qsa('.cart-line').forEach(line => {
      const number = Number(line.dataset.line);
      const count = Number(qs('.quantity-control span', line)?.textContent || 0);
      qs('[data-cart-minus]', line)?.addEventListener('click', () => changeCartLine(number, Math.max(0, count - 1)));
      qs('[data-cart-plus]', line)?.addEventListener('click', () => changeCartLine(number, count + 1));
      qs('[data-cart-remove]', line)?.addEventListener('click', () => changeCartLine(number, 0));
    });
  }

  function initCart() {
    qs('[data-close-cart]')?.addEventListener('click', closeAll);
    bindCartLines();
  }

  function initProduct() {
    const section = qs('[data-product-section]');
    if (!section) return;
    const variantsNode = qs('[data-product-json]', section);
    const form = qs('[data-product-form]', section);
    if (!variantsNode || !form) return;
    const variants = JSON.parse(variantsNode.textContent);
    const optionGroups = qsa('[data-option-position]', form);
    const variantInput = qs('[data-variant-id]', form);
    const submit = qs('[data-add-to-cart]', form);
    const price = qs('[data-price]', section);

    const chosenOptions = () => optionGroups.map(group => qs('.option-button.is-selected', group)?.dataset.optionValue);
    const updateVariant = () => {
      const chosen = chosenOptions();
      const variant = variants.find(item => item.options.every((value, index) => value === chosen[index]));
      if (!variant) {
        if (submit) { submit.disabled = true; qs('span', submit).textContent = 'Unavailable'; }
        return;
      }
      variantInput.value = variant.id;
      if (price) {
        price.innerHTML = `<span class="price__current">${money(variant.price)}</span>${variant.compare_at_price > variant.price ? `<s class="price__compare">${money(variant.compare_at_price)}</s>` : ''}`;
      }
      if (submit) {
        submit.disabled = !variant.available;
        qs('span', submit).textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      const url = new URL(window.location.href); url.searchParams.set('variant', variant.id); history.replaceState({}, '', url);
    };

    optionGroups.forEach(group => qsa('[data-option-value]', group).forEach(button => button.addEventListener('click', () => {
      qsa('[data-option-value]', group).forEach(el => { el.classList.remove('is-selected'); el.setAttribute('aria-pressed', 'false'); });
      button.classList.add('is-selected'); button.setAttribute('aria-pressed', 'true'); updateVariant();
    })));

    form.addEventListener('submit', async event => {
      event.preventDefault();
      if (submit) { submit.disabled = true; qs('span', submit).textContent = 'Adding…'; }
      try {
        const response = await fetch('/cart/add.js', { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
        if (!response.ok) throw new Error((await response.json()).description || 'Unable to add this piece');
        await refreshCart(true);
      } catch (error) {
        window.alert(error.message);
      } finally { updateVariant(); }
    });

    const recent = getRecent();
    const item = {
      title: section.dataset.productTitle || qs('h1', section)?.textContent.trim(),
      url: section.dataset.productUrl || window.location.pathname,
      image: section.dataset.productImage || qs('.product-gallery img', section)?.src || '',
      price: section.dataset.productPrice || qs('[data-price]', section)?.textContent.trim() || ''
    };
    const nextRecent = [item, ...recent.filter(old => old.url !== item.url)].slice(0, 6);
    localStorage.setItem('archiveRecentProducts', JSON.stringify(nextRecent));
  }

  function initProductModals() {
    qsa('[data-open-modal]').forEach(button => button.addEventListener('click', () => {
      const modal = qs(`[data-product-modal="${CSS.escape(button.dataset.openModal)}"]`);
      if (modal) { modal.hidden = false; lock(); }
    }));
    qsa('[data-close-modal]').forEach(button => button.addEventListener('click', closeAll));
  }

  function getRecent() {
    try { return JSON.parse(localStorage.getItem('archiveRecentProducts') || '[]'); } catch (_) { return []; }
  }

  function productResult(product) {
    const image = product.featured_image?.url || product.image || '';
    return `<a class="search-result" href="${escapeHTML(product.url)}">${image ? `<img src="${escapeHTML(image)}&width=300" alt="${escapeHTML(product.title)}">` : ''}<span>${escapeHTML(product.title)}</span>${product.price ? `<small>${money(product.price)}</small>` : ''}</a>`;
  }

  function renderRecent() {
    const holder = qs('[data-recently-viewed]');
    if (!holder) return;
    const recent = getRecent();
    holder.innerHTML = recent.length ? recent.map(item => `<a class="search-result" href="${escapeHTML(item.url)}">${item.image ? `<img src="${escapeHTML(item.image)}" alt="${escapeHTML(item.title)}">` : ''}<span>${escapeHTML(item.title)}</span><small>${escapeHTML(item.price)}</small></a>`).join('') : '<p>No recently viewed pieces.</p>';
  }

  function openSearch() {
    const shell = qs('[data-search-shell]');
    if (!shell) return;
    shell.hidden = false; lock(); renderRecent();
    window.setTimeout(() => qs('[data-predictive-search-input]', shell)?.focus(), 50);
  }

  function initSearch() {
    qs('[data-close-search]')?.addEventListener('click', closeAll);
    qs('[data-clear-recent]')?.addEventListener('click', () => { localStorage.removeItem('archiveRecentProducts'); renderRecent(); });
    const input = qs('[data-predictive-search-input]');
    const results = qs('[data-predictive-results]');
    if (!input || !results) return;
    let timer;
    input.addEventListener('input', () => {
      window.clearTimeout(timer);
      const query = input.value.trim();
      if (query.length < 2) { results.innerHTML = ''; return; }
      timer = window.setTimeout(async () => {
        try {
          const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`);
          const data = await response.json();
          results.innerHTML = (data.resources?.results?.products || []).map(productResult).join('') || '<p>No pieces found.</p>';
        } catch (_) { results.innerHTML = '<p>Search is temporarily unavailable.</p>'; }
      }, 220);
    });
  }

  function initCollection() {
    const form = qs('[data-collection-filters]');
    qs('[data-sort-select]')?.addEventListener('change', () => form?.requestSubmit());
    const grid = qs('[data-product-grid]');
    qsa('[data-grid-columns]').forEach(button => button.addEventListener('click', () => {
      qsa('[data-grid-columns]').forEach(el => el.classList.remove('is-active')); button.classList.add('is-active');
      const columns = button.dataset.gridColumns;
      grid?.classList.toggle('product-grid--4', columns === '4');
      localStorage.setItem('archiveGridColumns', columns);
    }));
    const saved = localStorage.getItem('archiveGridColumns');
    if (saved) qs(`[data-grid-columns="${saved}"]`)?.click();
  }

  function initRecommendations() {
    qsa('product-recommendations[data-url]').forEach(async element => {
      try {
        const response = await fetch(element.dataset.url);
        const html = document.createElement('div'); html.innerHTML = await response.text();
        const fresh = qs('product-recommendations', html);
        if (fresh?.innerHTML.trim()) { element.innerHTML = fresh.innerHTML; initProductCards(element); }
      } catch (_) { /* Recommendations are non-critical. */ }
    });
  }

  function initDragScroll() {
    qsa('[data-drag-scroll]').forEach(track => {
      let down = false, start = 0, scroll = 0;
      track.addEventListener('pointerdown', event => { down = true; start = event.clientX; scroll = track.scrollLeft; track.classList.add('is-dragging'); track.setPointerCapture(event.pointerId); });
      track.addEventListener('pointermove', event => { if (down) track.scrollLeft = scroll - (event.clientX - start); });
      const end = () => { down = false; track.classList.remove('is-dragging'); };
      track.addEventListener('pointerup', end); track.addEventListener('pointercancel', end);
    });
  }

  function initEntryGate() {
    const gate = qs('[data-entry-gate]');
    if (!gate) return;
    if (sessionStorage.getItem('archiveEntered') === 'true') { gate.remove(); return; }
    const track = qs('[data-entry-track]', gate); const handle = qs('[data-entry-handle]', gate);
    let dragging = false, startX = 0, max = 0;
    const enter = () => { sessionStorage.setItem('archiveEntered', 'true'); gate.classList.add('is-dismissed'); window.setTimeout(() => gate.remove(), 900); };
    const move = event => {
      if (!dragging) return;
      const x = Math.min(max, Math.max(0, event.clientX - startX));
      handle.style.transform = `translateX(${x}px)`;
      if (x > max * .86) { dragging = false; enter(); }
    };
    handle.addEventListener('pointerdown', event => { dragging = true; max = track.clientWidth - handle.offsetWidth - 12; startX = event.clientX; handle.setPointerCapture(event.pointerId); });
    handle.addEventListener('pointermove', move);
    handle.addEventListener('pointerup', () => { if (dragging) handle.style.transform = ''; dragging = false; });
    handle.addEventListener('click', enter);
  }

  function initPassword() {
    const dialog = qs('[data-password-dialog]');
    qs('[data-password-open]')?.addEventListener('click', () => { if (dialog) dialog.hidden = false; });
    qs('[data-password-close]')?.addEventListener('click', () => { if (dialog) dialog.hidden = true; });
    const countdown = qs('[data-countdown]');
    if (!countdown) return;
    const target = new Date(countdown.dataset.countdown).getTime();
    const update = () => {
      const difference = Math.max(0, target - Date.now());
      const days = Math.floor(difference / 86400000);
      const hours = Math.floor(difference % 86400000 / 3600000);
      const minutes = Math.floor(difference % 3600000 / 60000);
      const seconds = Math.floor(difference % 60000 / 1000);
      qs('[data-days]', countdown).textContent = String(days).padStart(2, '0');
      qs('[data-hours]', countdown).textContent = String(hours).padStart(2, '0');
      qs('[data-minutes]', countdown).textContent = String(minutes).padStart(2, '0');
      qs('[data-seconds]', countdown).textContent = String(seconds).padStart(2, '0');
    };
    update(); window.setInterval(update, 1000);
  }

  document.addEventListener('click', event => {
    if (event.target.matches('.modal-shell,[data-site-scrim]')) closeAll();
  });
  document.addEventListener('keydown', event => { if (event.key === 'Escape') closeAll(); });

  initHeader(); initPageTransition(); initProductCards(); initCart(); initProduct(); initProductModals();
  initSearch(); initCollection(); initRecommendations(); initDragScroll(); initEntryGate(); initPassword();
})();
